import { useContext, useEffect, useState } from "react";
import TagManager from "react-gtm-module";
import {
  logCartView,
  logShopPageView,
  logProductViewedEvent,
} from "@pepdirect/v3/helpers/analyticsLogger";
import { LogPageViewParams } from "@pepdirect/v3/types";
import { CartFragment, VariantFragment } from "services/graphql/generated";
import { AnalyticsSessionParamsContext } from "context/analyticsSessionParams";
import { LogContextValueInterface } from "context/log";
import { isProductionReady } from "config";

export function useLogPageProvider(
  currentUserId: string | null
): LogContextValueInterface {
  /**
   * Wrapper for logPageView, so we don't need to
   * write useEffect on every page.
   * @param key some unique id to identify page,
   * events are expected in a specific order given below
   * pageView -> gtm.js -> productView/cartView
   * for example, page url/slug/whatnot,
   * otherwise it won't trigger when for example
   * route changes from/kit/kit-1 to /kit/kit-2
   */
  const useLogPageView = (
    params: LogPageViewParams,
    product?: VariantFragment | null,
    cart?: CartFragment | null,
    key?: string
  ): void => {
    const [pageViewLoaded, setPageViewLoaded] = useState(false);
    const { analyticsSessionParams } = useContext(
      AnalyticsSessionParamsContext
    );

    const loadGtm = (shouldLoadGtm: boolean) => {
      isProductionReady &&
        shouldLoadGtm &&
        TagManager.initialize({ gtmId: "GTM-N5CBPB2" });
    };

    useEffect(() => {
      if (analyticsSessionParams) {
        logShopPageView(
          currentUserId,
          { ...params, sessionAtributes: analyticsSessionParams },
          "pantry shop",
          loadGtm
        );
        setPageViewLoaded(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key, analyticsSessionParams]);

    useEffect(() => {
      if (cart && pageViewLoaded) {
        logCartView(currentUserId, cart);
      }
    }, [cart, pageViewLoaded]);

    useEffect(() => {
      if (product && pageViewLoaded) {
        logProductViewedEvent(currentUserId, product);
      }
    }, [product, pageViewLoaded]);
  };

  return { useLogPageView };
}
